import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-hot-toast';
import { io } from 'socket.io-client';
import AppBookingsContext from './AppBookingsContext';
import { appBookingService } from '../../services/appBookings/appBookingService';

const SERVER_URL = process.env.REACT_APP_SERVER_URL || 'http://localhost:8000';

export const AppBookingsState = ({ children }) => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const socketRef = useRef(null);

  const fetchBookings = useCallback(async (options = { showToast: true }) => {
    const token = localStorage.getItem('token_agents');
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const data = await appBookingService.fetchBookings();
      if (data?.success) {
        setBookings(data.data || []);
      } else if (Array.isArray(data)) {
        setBookings(data);
      }
    } catch (error) {
      console.error('Failed to fetch app bookings:', error);
      if (options?.showToast !== false) {
        toast.error('Failed to load in-app bookings');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Eager fetching on root mount removed.
    // Bookings are fetched on-demand when the agent visits /bookings (AppBookingsPage).
    try {
      const token = localStorage.getItem('token_agents');
      const userId = localStorage.getItem('user_id');
      const agentId = userId;

      if (token && agentId) {
        socketRef.current = io(SERVER_URL, {
          transports: ['websocket', 'polling'],
          reconnectionAttempts: 5
        });

        socketRef.current.on('connect', () => {
          socketRef.current.emit('join-agent-home', { agentId });
        });

        socketRef.current.on('connect_error', (error) => {
          console.error('Socket Connection Error:', error);
        });

        socketRef.current.on('new-app-booking', (newBooking) => {
          if (newBooking) {
            setBookings((prev) => [newBooking, ...prev]);
            toast.success(`New in-app booking received: #${newBooking.booking_id || ''}!`, {
              duration: 5000,
              icon: '🎉'
            });
          }
        });
      }
    } catch (err) {
      console.error('Socket setup error:', err);
    }

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }, []);

  const value = {
    bookings,
    setBookings,
    loading,
    setLoading,
    fetchBookings
  };

  return (
    <AppBookingsContext.Provider value={value}>
      {children}
    </AppBookingsContext.Provider>
  );
};

export default AppBookingsState;
